import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

interface DropdownOptions {
  [key: string]: string;
}

@Component({
  selector: 'ut-academy-dropdown',
  templateUrl: './academy-dropdown.component.html',
  styleUrls: ['./academy-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AcademyDropdownComponent implements OnInit {
  @Input() dropdownLabel = '';
  @Input() dropdownId = 'dropdown';
  @Input() options: DropdownOptions = {};
  @Input() model = '';
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onChange = new EventEmitter<string>();

  @ViewChild('triggerButton', { static: true })
    triggerButton: ElementRef<HTMLButtonElement>;

  dropdownShown = false;
  displayLabel = '';
  selectedItemValue = '';
  optionKeys: string[] = [];

  private readonly KEY_CODES = {
    TAB: 9,
    RETURN: 13,
    ESC: 27,
    SPACE: 32,
    UP: 38,
    DOWN: 40,
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.optionKeys = Object.keys(this.options);

    if (!this.model && this.optionKeys.length > 0) {
      this.model = this.optionKeys[0];
      this.selectedItemValue = this.optionKeys[0];
    } else {
      this.selectedItemValue = this.model;
    }

    this.displayLabel = this.options[this.model] || '';
  }

  toggleDropdown(): void {
    this.dropdownShown = !this.dropdownShown;

    if (this.dropdownShown) {
      this.cdr.markForCheck();
      setTimeout(() => {
        this.focusSelectedItem();
      }, 10);
    } else {
      this.selectedItemValue = this.model;
      this.triggerButton.nativeElement.focus();
      this.cdr.markForCheck();
    }
  }

  setValue(value: string): void {
    const oldValue = this.model;
    this.displayLabel = this.options[value];
    this.model = value;
    this.dropdownShown = false;

    if (oldValue !== value) {
      this.onChange.emit(value);
    }

    this.triggerButton.nativeElement.focus();
    this.cdr.markForCheck();
  }

  onOptionClick(value: string): void {
    this.setValue(value);
  }

  onOptionKeydown(event: KeyboardEvent, value: string): void {
    const codes = [
      this.KEY_CODES.TAB,
      this.KEY_CODES.RETURN,
      this.KEY_CODES.ESC,
      this.KEY_CODES.SPACE,
      this.KEY_CODES.UP,
      this.KEY_CODES.DOWN,
    ];

    if (codes.includes(event.keyCode)) {
      switch (event.keyCode) {
        case this.KEY_CODES.SPACE:
        case this.KEY_CODES.RETURN:
          event.preventDefault();
          this.setValue(value);
          break;
        case this.KEY_CODES.UP:
          event.preventDefault();
          this.focusNextListItem('up');
          break;
        case this.KEY_CODES.DOWN:
          event.preventDefault();
          this.focusNextListItem('down');
          break;
        case this.KEY_CODES.ESC:
        case this.KEY_CODES.TAB:
          this.toggleDropdown();
          break;
        default:
          break;
      }
    }
  }

  onOptionBlur(event: FocusEvent): void {
    if (event.relatedTarget === null) {
      this.toggleDropdown();
    }
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: HTMLElement): void {
    if (this.dropdownShown && !this.elementRef.nativeElement.contains(target)) {
      this.dropdownShown = false;
      this.selectedItemValue = this.model;
      this.cdr.markForCheck();
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    if (this.dropdownShown) {
      this.toggleDropdown();
    }
  }

  private focusNextListItem(direction: 'up' | 'down'): void {
    const currentItemIndex = this.optionKeys.indexOf(this.selectedItemValue);
    let nextIndex: number;

    if (direction === 'down') {
      nextIndex = currentItemIndex < (this.optionKeys.length - 1)
        ? currentItemIndex + 1
        : currentItemIndex;
    } else {
      nextIndex = currentItemIndex > 0
        ? currentItemIndex - 1
        : currentItemIndex;
    }

    if (nextIndex !== currentItemIndex) {
      this.selectedItemValue = this.optionKeys[nextIndex];
      const nextListItem = document.getElementById(`${this.dropdownId}-option-${nextIndex}`);
      if (nextListItem) {
        nextListItem.focus();
      }
    }
  }

  private focusSelectedItem(): void {
    const currentItemIndex = this.optionKeys.indexOf(this.selectedItemValue);
    const selectedItem = document.getElementById(`${this.dropdownId}-option-${currentItemIndex}`);
    if (selectedItem) {
      selectedItem.focus();
    }
  }
}
